import cx from 'classnames';
import elementResizeDetectorMaker from 'element-resize-detector';
import { throttle } from 'lodash';
import { ThreeDots, ThreeDotsProps } from 'platform-components';
import React, { createContext, ReactElement } from 'react';
import ReactDOM from 'react-dom';

import DotNotification from 'common.components/dot-notification/dot-notification';

import './adaptive-dots.scss';

type OmittedProps = 'view';

interface Props extends Omit<ThreeDotsProps, OmittedProps> {
    children: Array<ReactElement>;
    mix?: string;
    popupProps?: Record<string, any>; // TODO: тайпинги для попапа
}

export interface AdaptiveDotsItemContext {
    isVisible: boolean;
}

// eslint-disable-next-line no-redeclare
export const AdaptiveDotsItemContext = createContext<AdaptiveDotsItemContext>({
    isVisible: true,
});

const THROTTLE_TIMEOUT_MS = 300;
const DOTS_CONTROL_WIDTH = 40;

const erd = isSSR
    ? null
    : elementResizeDetectorMaker({
          strategy: 'scroll',
      });

class AdaptiveDots extends React.PureComponent<Props> {
    state = {
        countVisibleItems: 0,
        shouldRenderContent: true,
    };

    componentDidMount(): void {
        if (this.containerRef.current && erd) {
            erd.listenTo(this.containerRef.current, throttle(this.recalculateItemsLocation, THROTTLE_TIMEOUT_MS));
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        const { children: prevChildren } = prevProps;
        const { children } = this.props;

        if (prevChildren.length !== children.length) {
            // очищаем пустые ref из списка
            this.itemRefs = this.itemRefs.filter(Boolean);
        }
    }

    componentWillUnmount(): void {
        if (erd) {
            erd.uninstall(this.containerRef.current as HTMLElement);
        }
    }

    static Item = ThreeDots.Item;
    static TYPES = ThreeDots.TYPES;

    private containerRef: React.RefObject<HTMLDivElement> = React.createRef();
    private itemRefs: React.ReactInstance[] = [];

    onItemRef = (ref: React.ReactInstance, itemIndex: number): void => {
        this.itemRefs[itemIndex] = ref;

        if (ref) {
            const isLastChild = itemIndex === this.props.children.length - 1;

            if (isLastChild) {
                this.recalculateItemsLocation();
            }
        }
    };

    recalculateItemsLocation = (): void => {
        // ресайз может произойти когда не все компоненты примаунтились, тогда не нужно его обрабатывать
        // т.к. обработка произойдет при маунте последнего итема
        if (!this.containerRef.current || !this.itemRefs.every(Boolean)) {
            return;
        }

        const containerWidth = this.containerRef.current.offsetWidth;

        if (containerWidth === 0) {
            this.setState({
                countVisibleItems: 0,
                shouldRenderContent: false,
            });

            return;
        }

        let newCountVisibleItems: number = 0;
        for (let i = this.itemRefs.length - 1; i >= 0; i -= 1) {
            const ref = this.itemRefs[i];

            const element = ReactDOM.findDOMNode(ref);

            if (element instanceof HTMLElement) {
                const { offsetLeft, scrollWidth } = element;

                // если помещаются не все итемы - нужно показывать точки
                const shouldShowDots = i !== this.itemRefs.length - 1;
                const dotsWidth = shouldShowDots ? DOTS_CONTROL_WIDTH : 0;

                const rightBorder = offsetLeft + scrollWidth + dotsWidth;

                if (rightBorder < containerWidth) {
                    newCountVisibleItems = i + 1;
                    break;
                }
            }
        }

        this.setState({
            countVisibleItems: newCountVisibleItems,
            shouldRenderContent: true,
        });
    };

    render() {
        if (isSSR) {
            return null;
        }

        const { children, mix, ...otherProps } = this.props;
        const { countVisibleItems, shouldRenderContent } = this.state;

        const visibleItems = children.slice(0, countVisibleItems);
        const hiddenInDots = children.slice(countVisibleItems, children.length) as Array<ReactElement>;

        const shouldShowDotNotification = hiddenInDots.some(({ props }) => {
            const hiddenItem = props.children;

            return (
                hiddenItem.props.shouldShowDotNotification ||
                (hiddenItem.props.notification && !hiddenItem.props.link.selected)
            );
        });

        return (
            <div className={cx('adaptive-dots', mix)} ref={this.containerRef}>
                {shouldRenderContent && (
                    <div className="adaptive-dots__content">
                        <AdaptiveDotsItemContext.Provider value={{ isVisible: true }}>
                            <div className="adaptive-dots__visible-items">{visibleItems}</div>
                        </AdaptiveDotsItemContext.Provider>
                        {hiddenInDots.length > 0 && (
                            <AdaptiveDotsItemContext.Provider value={{ isVisible: false }}>
                                <div className="adaptive-dots__wrapper">
                                    <ThreeDots {...otherProps} mix="adaptive-dots__dots-control">
                                        {hiddenInDots}
                                    </ThreeDots>
                                    {shouldShowDotNotification && (
                                        <DotNotification mix="adaptive-dots__dot-notification" />
                                    )}
                                </div>
                            </AdaptiveDotsItemContext.Provider>
                        )}
                    </div>
                )}
                <div className="adaptive-dots__hidden-block">
                    {React.Children.map<ReactElement, ReactElement>(children, (itemElement, itemIndex) =>
                        React.cloneElement(itemElement, {
                            ref: (ref: React.ReactInstance) => {
                                this.onItemRef(ref, itemIndex);
                            },
                        })
                    )}
                </div>
            </div>
        );
    }
}

export default AdaptiveDots;
